// src/components/layout/Footer.tsx
//
// Site footer. Brand info, contact details, legal links.

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Mail } from "lucide-react";

export function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="border-t border-border bg-surface-panel">
      <div className="container py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="space-y-3">
            <div className="flex flex-col leading-none">
              <span className="text-sm font-510 tracking-tight text-fg-primary">
                {t("app.name")}
              </span>
              <span className="text-[10px] font-510 uppercase tracking-wider text-benchmark-red">
                Distribution
              </span>
            </div>
            <p className="text-xs text-fg-tertiary leading-relaxed">{t("app.tagline")}</p>
          </div>

          {/* Contact */}
          <div className="space-y-2.5">
            <h3 className="text-xs font-510 uppercase tracking-wider text-fg-tertiary">
              {t("footer.contact", "Contact")}
            </h3>
            <ul className="space-y-1.5 text-xs text-fg-secondary">
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-3 w-3 flex-shrink-0 text-benchmark-teal-bright" strokeWidth={1.5} />
                <span>256, Chaussée de Marche<br />5100 Jambes, Belgium</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-3 w-3 flex-shrink-0 text-benchmark-teal-bright" strokeWidth={1.5} />
                <a href="tel:+3281224149" className="hover:text-fg-primary">
                  (+32) 81 22 41 49
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-3 w-3 flex-shrink-0 text-benchmark-teal-bright" strokeWidth={1.5} />
                <a href="mailto:info@benchmark-av.be" className="hover:text-fg-primary">
                  info@benchmark-av.be
                </a>
              </li>
            </ul>
          </div>

          {/* Opening hours */}
          <div className="space-y-2.5">
            <h3 className="text-xs font-510 uppercase tracking-wider text-fg-tertiary">
              {t("footer.openingHours", "Opening hours")}
            </h3>
            <ul className="space-y-1 text-xs text-fg-secondary">
              <li className="flex justify-between">
                <span>{t("footer.monday", "Monday")}</span>
                <span className="text-fg-tertiary">{t("footer.closed", "Closed")}</span>
              </li>
              <li className="flex justify-between">
                <span>{t("footer.tueSat", "Tue - Sat")}</span>
                <span>10:00 - 18:00</span>
              </li>
              <li className="flex justify-between">
                <span>{t("footer.sunday", "Sunday")}</span>
                <span className="text-fg-tertiary">{t("footer.closed", "Closed")}</span>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="space-y-2.5">
            <h3 className="text-xs font-510 uppercase tracking-wider text-fg-tertiary">
              {t("footer.legal", "Legal")}
            </h3>
            <ul className="space-y-1 text-xs text-fg-secondary">
              <li>
                <Link to="/legal/privacy" className="hover:text-fg-primary">
                  {t("footer.privacy", "Privacy policy")}
                </Link>
              </li>
              <li>
                <Link to="/legal/terms" className="hover:text-fg-primary">
                  {t("footer.terms", "Terms of sale")}
                </Link>
              </li>
              <li>
                <Link to="/legal/cookies" className="hover:text-fg-primary">
                  {t("footer.cookies", "Cookies")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-border pt-6 text-center text-[11px] text-fg-quaternary">
          © {new Date().getFullYear()} BENCHMARK AV Distribution · {t("footer.baseline", "Professional audio & video equipment")}
        </div>
      </div>
    </footer>
  );
}